import React, { useEffect, useState, useCallback } from 'react';
import {
  ShieldAlert, CreditCard, Smartphone, LogOut, CheckCircle2, MessageCircle,
  Mail, Loader2, AlertTriangle, Crown
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { subscriptionsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const fmtMoney = (amount, currency = 'xof') => {
  const v = Math.round(Number(amount) || 0).toLocaleString('fr-FR');
  return currency.toLowerCase() === 'xof' ? `${v} FCFA` : `${v} ${currency.toUpperCase()}`;
};

export const SubscriptionExpiredOverlay = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [agency, setAgency] = useState(null);
  const [activeTab, setActiveTab] = useState('stripe'); // stripe / mobile
  const [submitting, setSubmitting] = useState(false);

  // Mobile money form
  const [mobileForm, setMobileForm] = useState({
    plan: 'monthly',
    method: 'orange_money',
    sender_msisdn: '',
    reference: '',
    note: '',
  });
  const [mobileSent, setMobileSent] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [my, info] = await Promise.all([
        subscriptionsAPI.getMy().catch(() => ({ data: null })),
        subscriptionsAPI.getPaymentInfo().catch(() => ({ data: null })),
      ]);
      if (my?.data) {
        setAgency(my.data.agency || null);
        const planMap = my.data.plans || {};
        setPlans(Object.values(planMap));
      } else {
        // fallback : just list plans
        const r = await subscriptionsAPI.getPlans().catch(() => ({ data: [] }));
        setPlans(r.data || []);
      }
      setPaymentInfo(info?.data || null);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      // Don't show overlay on public/login pages
      const path = window.location.pathname || '';
      if (path.startsWith('/login') || path.startsWith('/agency-register')
          || path.startsWith('/tenant/login') || path.startsWith('/owner/login')
          || path.startsWith('/subscription/success')) {
        return;
      }
      setDetails(e.detail || {});
      setOpen(true);
      fetchData();
    };
    window.addEventListener('subscription-expired', handler);
    return () => window.removeEventListener('subscription-expired', handler);
  }, [fetchData]);

  if (!open) return null;

  const handleStripeCheckout = async (planKey) => {
    setSubmitting(true);
    try {
      const origin = window.location.origin;
      const r = await subscriptionsAPI.createCheckout(planKey, origin);
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('URL de paiement introuvable');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la creation du paiement');
    }
    setSubmitting(false);
  };

  const handleMobileNotify = async () => {
    if (!mobileForm.sender_msisdn.trim() || !mobileForm.reference.trim()) {
      toast.error('Numero expediteur et reference obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await subscriptionsAPI.notifyMobilePayment(mobileForm);
      setMobileSent(true);
      toast.success('Paiement declare. L\'administrateur va valider sous peu.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSubmitting(false);
  };

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    window.location.href = '/login';
  };

  const monthlyPlan = plans.find((p) => p.id === 'monthly');
  const annualPlan = plans.find((p) => p.id === 'annual');
  const selectedPlan = plans.find((p) => p.id === mobileForm.plan);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="subscription-expired-overlay"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full my-8 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1B3A4B] to-[#0d2937] text-white p-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4A745]/20 mb-3">
            <ShieldAlert className="w-9 h-9 text-[#D4A745]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold">Abonnement expire</h2>
          <p className="text-white/80 mt-2 text-sm md:text-base">
            {details?.message || "L'acces a la plateforme est suspendu jusqu'au renouvellement de votre abonnement."}
          </p>
          {agency && (
            <p className="text-xs text-[#D4A745] mt-2">
              Agence : <span className="font-semibold">{agency.name}</span>
              {agency.subscription_end_date && (
                <> · Expire le {new Date(agency.subscription_end_date).toLocaleDateString('fr-FR')}</>
              )}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {loading ? (
            <div className="py-10 text-center text-gray-500">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#1B3A4B]" />
              <p className="mt-2 text-sm">Chargement...</p>
            </div>
          ) : (
            <>
              {/* Tabs payment method */}
              <div className="flex gap-2 mb-5 border-b">
                <button
                  onClick={() => setActiveTab('stripe')}
                  data-testid="tab-stripe"
                  className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm flex items-center gap-2 transition ${
                    activeTab === 'stripe'
                      ? 'border-[#D4A745] text-[#1B3A4B]'
                      : 'border-transparent text-gray-500 hover:text-[#1B3A4B]'
                  }`}
                >
                  <CreditCard className="w-4 h-4" /> Carte bancaire
                </button>
                <button
                  onClick={() => setActiveTab('mobile')}
                  data-testid="tab-mobile-money"
                  className={`px-4 py-2 -mb-px border-b-2 font-medium text-sm flex items-center gap-2 transition ${
                    activeTab === 'mobile'
                      ? 'border-[#D4A745] text-[#1B3A4B]'
                      : 'border-transparent text-gray-500 hover:text-[#1B3A4B]'
                  }`}
                >
                  <Smartphone className="w-4 h-4" /> Mobile Money
                </button>
              </div>

              {activeTab === 'stripe' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Paiement instantane et securise via Stripe (Visa, Mastercard).
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {monthlyPlan && (
                      <div className="border-2 border-gray-200 rounded-xl p-5 hover:border-[#1B3A4B] transition"
                           data-testid="plan-monthly">
                        <h3 className="font-bold text-[#1B3A4B] text-lg">{monthlyPlan.name}</h3>
                        <p className="text-xs text-gray-500 mb-3">{monthlyPlan.description}</p>
                        <div className="text-3xl font-bold text-[#1B3A4B] mb-1">
                          {fmtMoney(monthlyPlan.amount, monthlyPlan.currency)}
                        </div>
                        <p className="text-xs text-gray-500 mb-4">/ {monthlyPlan.duration_days} jours</p>
                        <Button
                          onClick={() => handleStripeCheckout('monthly')}
                          disabled={submitting}
                          className="w-full bg-[#1B3A4B] hover:bg-[#0d2937] text-white"
                          data-testid="pay-monthly-btn"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                          Payer maintenant
                        </Button>
                      </div>
                    )}
                    {annualPlan && (
                      <div className="border-2 border-[#D4A745] rounded-xl p-5 relative"
                           data-testid="plan-annual">
                        <span className="absolute -top-2 right-4 bg-[#D4A745] text-white text-xs px-2 py-0.5 rounded-full font-semibold flex items-center">
                          <Crown className="w-3 h-3 mr-1" /> Recommande
                        </span>
                        <h3 className="font-bold text-[#1B3A4B] text-lg">{annualPlan.name}</h3>
                        <p className="text-xs text-gray-500 mb-3">{annualPlan.description}</p>
                        <div className="text-3xl font-bold text-[#1B3A4B] mb-1">
                          {fmtMoney(annualPlan.amount, annualPlan.currency)}
                        </div>
                        <p className="text-xs text-gray-500 mb-4">/ {annualPlan.duration_days} jours</p>
                        <Button
                          onClick={() => handleStripeCheckout('annual')}
                          disabled={submitting}
                          className="w-full bg-[#D4A745] hover:bg-[#b88a30] text-white"
                          data-testid="pay-annual-btn"
                        >
                          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                          Payer maintenant
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'mobile' && (
                <div className="space-y-4">
                  {mobileSent ? (
                    <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 text-center"
                         data-testid="mobile-sent-confirmation">
                      <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3" />
                      <h3 className="font-bold text-emerald-900 text-lg">Paiement declare avec succes</h3>
                      <p className="text-sm text-emerald-800 mt-2">
                        Votre paiement est en attente de validation par l'administrateur.
                        Vous recevrez une notification des l'activation de votre abonnement.
                      </p>
                      {paymentInfo?.whatsapp && (
                        <a
                          href={`https://wa.me/${(paymentInfo.whatsapp || '').replace(/\D/g, '')}?text=${encodeURIComponent('Bonjour, je viens d\'effectuer un paiement Mobile Money pour mon abonnement GAB\'S SERVICES. Reference: ' + mobileForm.reference)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center mt-4 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 text-sm"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" /> Notifier sur WhatsApp
                        </a>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Numéros de paiement */}
                      <div className="bg-amber-50 border-l-4 border-[#D4A745] rounded p-4">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-[#D4A745] flex-shrink-0 mt-0.5" />
                          <div className="text-sm text-gray-800">
                            <p className="font-semibold mb-2">Procedure de paiement Mobile Money</p>
                            <ol className="list-decimal ml-5 space-y-1 text-xs">
                              <li>Choisissez votre plan et envoyez le montant exact aux numeros ci-dessous</li>
                              <li>Notez la reference de transaction donnee par l'operateur</li>
                              <li>Remplissez le formulaire ci-dessous pour declarer votre paiement</li>
                              <li>L'administrateur valide sous 24h et reactive votre abonnement</li>
                            </ol>
                          </div>
                        </div>
                      </div>

                      {paymentInfo && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="border rounded-lg p-3 bg-orange-50 border-orange-200">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded">Orange Money</span>
                            </div>
                            <p className="text-lg font-mono font-bold text-orange-900">{paymentInfo.orange_money}</p>
                          </div>
                          <div className="border rounded-lg p-3 bg-yellow-50 border-yellow-200">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded">Jɛmɛnipay</span>
                            </div>
                            <p className="text-lg font-mono font-bold text-yellow-900">{paymentInfo.jemenipay}</p>
                          </div>
                        </div>
                      )}

                      {/* Form */}
                      <div className="space-y-3 border rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Plan choisi *</Label>
                            <Select value={mobileForm.plan}
                                    onValueChange={(v) => setMobileForm({ ...mobileForm, plan: v })}>
                              <SelectTrigger data-testid="mobile-plan-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {plans.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} - {fmtMoney(p.amount, p.currency)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-xs">Operateur *</Label>
                            <Select value={mobileForm.method}
                                    onValueChange={(v) => setMobileForm({ ...mobileForm, method: v })}>
                              <SelectTrigger data-testid="mobile-method-select"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="orange_money">Orange Money</SelectItem>
                                <SelectItem value="jemenipay">Jɛmɛnipay</SelectItem>
                                <SelectItem value="autre">Autre</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {selectedPlan && (
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 text-sm text-blue-900 text-center">
                            Montant a envoyer : <span className="font-bold">{fmtMoney(selectedPlan.amount, selectedPlan.currency)}</span>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Numero expediteur (votre numero MM) *</Label>
                          <Input value={mobileForm.sender_msisdn}
                                 onChange={(e) => setMobileForm({ ...mobileForm, sender_msisdn: e.target.value })}
                                 placeholder="Ex: +223 76 12 34 56"
                                 data-testid="mobile-sender-input" />
                        </div>
                        <div>
                          <Label className="text-xs">Reference de transaction *</Label>
                          <Input value={mobileForm.reference}
                                 onChange={(e) => setMobileForm({ ...mobileForm, reference: e.target.value })}
                                 placeholder="Ex: MP260509.1530.A12345"
                                 data-testid="mobile-reference-input" />
                        </div>
                        <div>
                          <Label className="text-xs">Note (facultatif)</Label>
                          <Textarea rows={2} value={mobileForm.note}
                                    onChange={(e) => setMobileForm({ ...mobileForm, note: e.target.value })}
                                    data-testid="mobile-note-input" />
                        </div>
                        <Button onClick={handleMobileNotify} disabled={submitting}
                                className="w-full bg-[#D4A745] hover:bg-[#b88a30] text-white"
                                data-testid="submit-mobile-payment-btn">
                          {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                          Declarer mon paiement
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Footer contact */}
              {paymentInfo && (
                <div className="mt-5 pt-4 border-t flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
                  <span className="font-semibold">Besoin d'aide ?</span>
                  {paymentInfo.whatsapp && (
                    <a href={`https://wa.me/${(paymentInfo.whatsapp || '').replace(/\D/g, '')}`}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center hover:text-emerald-700">
                      <MessageCircle className="w-3 h-3 mr-1" /> {paymentInfo.whatsapp}
                    </a>
                  )}
                  {paymentInfo.email && (
                    <a href={`mailto:${paymentInfo.email}`}
                       className="inline-flex items-center hover:text-[#1B3A4B]">
                      <Mail className="w-3 h-3 mr-1" /> {paymentInfo.email}
                    </a>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Bottom bar with logout */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
          <p className="text-xs text-gray-500">
            Connecte en tant que <span className="font-semibold">{user?.name || user?.email}</span>
          </p>
          <Button variant="outline" size="sm" onClick={handleLogout}
                  data-testid="overlay-logout-btn"
                  className="text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-1" /> Se deconnecter
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionExpiredOverlay;
